import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHover, useUpdate } from "ahooks";
import { memo } from "react";
import { useEditorContext } from "../EditorContext";
import Tree from "../tree";
import noContentTpl from "../NoContent/no-content";
import { useRowContext } from "./RowContext";
import { useDrag } from "ahooks";
import { useDrop } from "../hooks/useDragAndDrop";
import SelectedToolbar, { DragImg } from "../Common/SelectedToolbar";
import { JSON_TPL_MAPPING, getRowBackgroundImageConfig } from "./Row";
import DragItHere from "../Common/DragItHere";
import { toRGBA } from "../parser/utils";


export default memo(({ children, node, rowIndex }) => {
  const { shareRefs, removeChild, addChild, notifySubscriber } =
    useEditorContext();

  const { rowShareRefs } = useRowContext();

  const update = useUpdate();

  const delNode = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();
      removeChild(node.parent.children, e.currentTarget.dataset.id);
      if (node.parent.children.length === 0) {
        const subTree = new Tree(noContentTpl);
        addChild(node.parent, subTree.root, 0);
      }
      shareRefs.updateRows();
      shareRefs.selectedNode = null;
      notifySubscriber("NODE_SELECT");
      notifySubscriber("DATA_CHANGE");
    },
    [removeChild, shareRefs, node.parent.children]
  );
  

  const duplicateNode = useCallback(
    (e: React.SyntheticEvent) => {
      const id = e.currentTarget.dataset.id;
      const foundIndex = node.parent.children.findIndex((n) => n.id === id);

      const copyJSON = node.toJSON({ isDuplcate: true });
      const subTree = new Tree(copyJSON);
      addChild(node.parent, subTree.root, foundIndex + 1);
      shareRefs.updateRows();
    },
    [node, shareRefs, addChild]
  );

  const { dropProps, isHovering: isDropHovering, hoverPos } = useDrop({
    onDrop: (type, data) => {
      if (shareRefs.dragToNodeType === "col") return;
      if (shareRefs.dropedInCol) {
        return shareRefs.dropedInCol = false;
      }
      if (shareRefs.movingExist) {
        removeChild(shareRefs.movingParentNode.children, shareRefs.movingNode.id);
        node.parent.addChild(shareRefs.movingNode, hoverPos === "top" ? rowIndex : rowIndex + 1);
        shareRefs.movingExist = false;
        shareRefs.movingParentNode = null;
        shareRefs.movingNode = null;
      } else {
        let subTree;
        if (data) {
          subTree = new Tree(JSON.parse(data));
        } else {
          const tplData = JSON_TPL_MAPPING[type];

          subTree = new Tree(
            tplData.type === "row"
              ? tplData
              : {
                type: "row",
                style: "",
                children: [tplData],
              }
          );
        }
        node.parent.addChild(subTree.root, hoverPos === "top" ? rowIndex : rowIndex + 1);
      }

      shareRefs.dragToNodeType = "";
      // remove no content block;
      // const noContentBlockIndex = node.parent.children.findIndex(
      //   (row) => row.id === "no-content-row"
      // );
      // if (noContentBlockIndex > -1) {
      //   node.parent.children.splice(noContentBlockIndex, 1);
      // }

      shareRefs.updateRows();
    },
  });

  const [isDraging, setIsDraging] = useState(false);

  const hoverRef = useRef();
  const isHovering = useHover(hoverRef);

  const draggingRef = useRef<HTMLElement>(null);
  useDrag("", draggingRef, {
    onDragStart: (e) => {
      shareRefs.dragToNodeType = "row";
      shareRefs.movingExist = true;
      shareRefs.movingNode = node;
      shareRefs.movingParentNode = node.parent;
      setIsDraging(true);
    },
    onDragEnd: () => {
      setIsDraging(false);
      shareRefs.movingExist = false;
      shareRefs.movingParentNode = null;
      shareRefs.movingNode = null;
    },
    dragImage: {
      image: DragImg,
    },
  });

  const updateSelectContent = useCallback(
    (data = {}) => {
      if (node.type === "no-content") return;
      if (data.id && node.id !== data.id) {
        node.id = data.id;
      }
      Object.assign(node.data, data);
      rowShareRefs.updateCurrentRow();
    },
    [node, update]
  );


  useEffect(() => {
    rowShareRefs.updateSelectRowContent = updateSelectContent
  }, [updateSelectContent]);

  const handleClick = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();
      if (node.type === "no-content") return;
      if (shareRefs.selectedNode) {
        shareRefs.selectedNode = node;
        // remove prev selection status;
        shareRefs.updateSelectContent();
        updateSelectContent();
        shareRefs.updateSelectContent = updateSelectContent;
      } else {
        shareRefs.selectedNode = node;
        shareRefs.updateSelectContent = updateSelectContent;
        updateSelectContent();
      }
      notifySubscriber("NODE_SELECT");
    },
    [updateSelectContent, shareRefs, notifySubscriber, node]
  );

  const computedHover = isHovering && !rowShareRefs.hoverInCnt;
  const isSelectedNode =
    shareRefs.selectedNode && node.id === shareRefs.selectedNode.id;

  const {
    dup = true,
    del = true,
    applyBackgroundTo = "row",
    rowBackgroundColor,
    applyImageTo = "row",
  } = node.data || {};


  const dragHovering = isDropHovering && shareRefs.dragToNodeType === "row";

  let backgroundColor = "transparent";
  if (applyBackgroundTo === "row") {
    backgroundColor = toRGBA(rowBackgroundColor);
  } else if (applyBackgroundTo === "content") {
    backgroundColor = toRGBA(shareRefs.tree.root.data.backgroundColor); 
  }

  return (
    <div
      ref={hoverRef}
      className="rip-mm-row"
      onClick={handleClick}
      style={{
        display: "flex",
        justifyContent: shareRefs.tree.root.data.contentAreaAlignment || "center",
        position: "relative",
        outline: computedHover || isSelectedNode ? "#fc679a solid 2px" : "none",
        outlineOffset: -2,
        opacity: isDraging ? 0.6 : 1,
        backgroundColor,
        ...(applyImageTo === "row" ? getRowBackgroundImageConfig(node.data) : {})
      }}
      {...dropProps}
    >
      {children}
      
      <SelectedToolbar
        isSelectedNode={isSelectedNode}
        draggingRef={draggingRef}
        id={node.id}
        canDuplicate={dup}
        onDuplicate={duplicateNode}
        canDelete={del}
        onDelete={delNode}
      />
      <DragItHere hoverPos={hoverPos} dragHovering={dragHovering} />
    </div>
  );
});
